import React, { useState, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import { localizedPath } from '../utils/routes';
import SEOHead from '../components/SEOHead';

const API = process.env.REACT_APP_API_URL || '/api';

/** Format phone for display: +15143272107 → (514) 327-2107 */
function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

export default function Contact() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { mc } = useMarketingCopy();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const [form, setForm] = useState({ subject: '', message: '' });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const fileInputRef = useRef(null);

  const validateForm = () => {
    const errors = {};
    if (!form.subject.trim()) errors.subject = t.contact.form.subjectLabel + ' required';
    else if (form.subject.trim().length > 200) errors.subject = t.contact.form.subjectTooLong;

    if (!form.message.trim() || form.message.trim().length < 5) errors.message = t.contact.form.messageTooShort;
    else if (form.message.trim().length > 2000) errors.message = t.contact.form.messageTooLong;

    return errors;
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files).slice(0, 3);
    setFiles(selected);
    const newPreviews = selected.map(f => ({ name: f.name, url: URL.createObjectURL(f) }));
    setPreviews(newPreviews);
  };

  const removeFile = (idx) => {
    const newFiles = [...files];
    const newPreviews = [...previews];
    URL.revokeObjectURL(newPreviews[idx].url);
    newFiles.splice(idx, 1);
    newPreviews.splice(idx, 1);
    setFiles(newFiles);
    setPreviews(newPreviews);
  };

  const clearFiles = () => {
    previews.forEach(p => URL.revokeObjectURL(p.url));
    setFiles([]);
    setPreviews([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });
    setFieldErrors({});

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      // Upload attachments first if any
      let attachmentIds = [];
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach(f => formData.append('files', f));
        const uploadRes = await axios.post(`${API}/contact/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        attachmentIds = uploadRes.data.map(a => a.id);
      }

      await axios.post(`${API}/contact`, {
        subject: form.subject.trim(),
        message: form.message.trim(),
        attachment_ids: attachmentIds
      });
      setStatus({ type: 'success', message: t.contact.form.success });
      setForm({ subject: '', message: '' });
      clearFiles();
    } catch (err) {
      // Handle structured validation errors from backend
      if (err.response?.data?.errors) {
        const backendErrors = {};
        err.response.data.errors.forEach(e => { backendErrors[e.field] = e.message; });
        setFieldErrors(backendErrors);
      } else {
        setStatus({ type: 'error', message: err.response?.data?.error || t.contact.form.error });
      }
    } finally {
      setLoading(false);
    }
  };

  const userName = user ? `${user.first_name} ${user.last_name}`.trim() : '';
  const userEmail = user?.email || '';
  const userPhone = user?.phone ? formatPhoneDisplay(user.phone) : '';

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', paddingTop: 'var(--cjpa-nav-offset)' }}>
      <SEOHead page="contact" />
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 2rem)' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 5rem)' }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '1rem' }}>
            {mc('contact', 'subtitle', t.contact.subtitle)}
          </div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(3rem, 6vw, 5rem)', color: '#E8ECF0', fontWeight: 300 }}>
            {t.contact.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.contact.titleHighlight}</span>
          </h1>
        </div>

        {/* Contact Info Cards */}
        <div className="cjpa-contact-grid" style={{ background: 'rgba(148,163,184,0.1)', marginBottom: 'clamp(2rem, 4vw, 5rem)' }}>
          <div style={{ background: '#111827', padding: '2.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '1rem' }}>
              {t.contact.visitUs}
            </div>
            <div style={{ color: '#D42B2B', fontSize: '1rem', lineHeight: 1.7, fontFamily: "'Rajdhani', sans-serif" }}>
              {t.business.address}
            </div>
            <a
              href={`https://www.google.com/maps/dir//${encodeURIComponent(t.business.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm mt-3"
            >
              {t.contact.directions}
            </a>
          </div>

          <div style={{ background: '#111827', padding: '2.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '1rem' }}>
              {t.contact.callUs}
            </div>
            <a href={`tel:${mc('contact', 'phone1', t.business.phone)}`} style={{
              color: '#D42B2B', fontSize: '1.5rem', textDecoration: 'none',
              fontFamily: "'Rajdhani', sans-serif", display: 'block'
            }}>
              {mc('contact', 'phone1', t.business.phone)}
            </a>
            <a href={`tel:${mc('contact', 'phone2', t.business.phone2)}`} style={{
              color: '#D42B2B', fontSize: '1.2rem', textDecoration: 'none',
              fontFamily: "'Rajdhani', sans-serif", display: 'block', marginTop: '0.4rem'
            }}>
              {mc('contact', 'phone2', t.business.phone2)}
            </a>
            <div style={{ color: '#94A3B8', fontSize: '0.8rem', marginTop: '1rem' }}>
              {t.business.hours.monday}–{t.business.hours.friday}<br />
              {t.business.hours.weekdaysTime}
            </div>
          </div>

          <div style={{ background: '#111827', padding: '2.5rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '1rem' }}>
              {t.contact.emailUs}
            </div>
            <a href={`mailto:${t.business.email}`} style={{
              color: '#D42B2B', fontSize: '1.1rem', textDecoration: 'none',
              fontFamily: "'Rajdhani', sans-serif", display: 'block'
            }}>
              {t.business.email}
            </a>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="row" style={{ alignItems: 'start' }}>

          {/* Contact Form */}
          <div className="col-md-6">
            <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '3rem' }}>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '2rem' }}>
                {mc('contact', 'form.title', t.contact.form.title)}
              </h2>

              {!user ? (
                /* Login required prompt */
                <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                  <div style={{ color: '#94A3B8', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                    {t.contact.form.loginRequired}
                  </div>
                  <Link to={lp('login')} className="btn btn-primary" style={{ padding: '0.75rem 2rem' }}>
                    {t.contact.form.loginButton}
                  </Link>
                </div>
              ) : (
                <>
                  {status.message && (
                    <div className={`alert ${status.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                      {status.message}
                    </div>
                  )}

                  {/* Read-only user identity */}
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.1)' }}>
                    <div className="mb-2">
                      <label className="form-label" style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem' }}>{t.contact.form.nameLabel}</label>
                      <input value={userName} disabled className="form-control" style={{ opacity: 0.8 }} />
                    </div>
                    <div className="row">
                      <div className="col-12 col-md-6 mb-2">
                        <label className="form-label" style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem' }}>{t.contact.form.emailLabel}</label>
                        <input value={userEmail} disabled className="form-control" style={{ opacity: 0.8 }} />
                      </div>
                      <div className="col-12 col-md-6 mb-2">
                        <label className="form-label" style={{ fontSize: '0.75rem', color: '#94A3B8', marginBottom: '0.25rem' }}>{t.contact.form.phoneLabel}</label>
                        <input value={userPhone || '\u2014'} disabled className="form-control" style={{ opacity: 0.8 }} />
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">{t.contact.form.subjectLabel}</label>
                      <input required value={form.subject} onChange={e => setForm({...form, subject: e.target.value})}
                        maxLength={200}
                        className={`form-control ${fieldErrors.subject ? 'is-invalid' : ''}`}
                      />
                      {fieldErrors.subject && <div className="invalid-feedback">{fieldErrors.subject}</div>}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t.contact.form.messageLabel}</label>
                      <textarea required rows={6} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                        placeholder={t.contact.form.messagePlaceholder}
                        maxLength={2000}
                        className={`form-control ${fieldErrors.message ? 'is-invalid' : ''}`}
                      />
                      {fieldErrors.message && <div className="invalid-feedback">{fieldErrors.message}</div>}
                      <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#64748B', marginTop: '0.25rem' }}>
                        {form.message.length}/2000 {t.contact.form.charCount}
                      </div>
                    </div>

                    {/* Attachment section */}
                    <div className="mb-4">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        multiple
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={loading || files.length >= 3}
                        style={{
                          background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                          color: '#94A3B8', padding: '0.5rem 1rem', fontSize: '0.8rem',
                          cursor: loading || files.length >= 3 ? 'not-allowed' : 'pointer',
                          opacity: files.length >= 3 ? 0.5 : 1
                        }}
                      >
                        {t.contact.form.attachImages || 'Attach Images'}
                      </button>
                      <span style={{ fontSize: '0.7rem', color: '#64748B', marginLeft: '0.5rem' }}>
                        {t.contact.form.attachHint || 'Max 3 images, 5MB each (JPEG, PNG, GIF, WebP)'}
                      </span>
                      {previews.length > 0 && (
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                          {previews.map((p, idx) => (
                            <div key={idx} style={{ position: 'relative' }}>
                              <img
                                src={p.url}
                                alt={p.name}
                                style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(148,163,184,0.2)' }}
                              />
                              <button
                                type="button"
                                onClick={() => removeFile(idx)}
                                aria-label={t.common.close}
                                style={{
                                  position: 'absolute', top: '-6px', right: '-6px',
                                  background: '#D42B2B', border: 'none', color: '#fff',
                                  width: '20px', height: '20px', borderRadius: '50%',
                                  fontSize: '0.7rem', lineHeight: '20px', textAlign: 'center',
                                  cursor: 'pointer', padding: 0
                                }}
                              >&times;</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button type="submit" disabled={loading} className="btn btn-primary w-100" style={{ padding: '1rem' }}>
                      {loading ? t.contact.form.sending : t.contact.form.send}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>

          {/* Google Maps */}
          <div className="col-md-6">
            <div>
              <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '1.5rem', height: 'clamp(300px, 50vw, 600px)' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2793.6899889562944!2d-73.61855842340717!3d45.59678302583074!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4cc9203b2e4b5b71%3A0x5de8d5c0c5c5e8e8!2s10011%20Av.%20de%20Rome%2C%20Montr%C3%A9al-Nord%2C%20QC%20H1H%204N3!5e0!3m2!1sen!2sca!4v1644000000000!5m2!1sen!2sca"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="M\u00e9canique Auto CJPA Location"
                ></iframe>
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
